import { Input, message, Switch, Tooltip } from 'antd';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

export type AvatarFormData = {
  avatarName: string;
  gender: number;
  voiceClone: boolean;
  prompt: string;
  negative_prompt: string;
};
interface AvatarFormProps {
  duration: number | string;
  mode: Exclude<AvatarMode, ''>;
  url: string;
  onChange: (data: AvatarFormData) => void;
}
import { usePartial } from '@/hooks/usePartial';
import { t } from '@/utils/helper/intl';

import NoIcon from '../../icon/no';
import YesIcon from '../../icon/yes';
import styles from '../../style.module.scss';
import { AvatarMode } from '..';

export const defaultAvatarData = {
  avatarName: '',
  gender: 0,
  voiceClone: false,
  prompt: '',
  negative_prompt: '',
};

const AvatarName: React.FC<{
  name: string;
  onChange: (name: string) => void;
}> = memo((props) => {
  return (
    <div className="flex-1 bg-inherit rounded-[8px] gap-[16px]">
      <div className="text-[14px] font-[500] mb-[5px]">
        {t('merror.createava.name')} :
      </div>
      <div className="flex grow items-center border-[#43444A] border-[1px] rounded-[6px] py-[10px] px-[22px] gap-[16px]">
        <Input
          value={props.name}
          className={styles['upload-avatar-input-new']}
          style={{ height: 32, resize: 'none', fontSize: 14 }}
          maxLength={20}
          onChange={(e) => {
            props.onChange(e.target.value);
          }}
        />
        <span className="font-number text-[#a3a3a3] text-[14px] tracking-[0.24px] font-[400]">
          {props.name.length}/20
        </span>
      </div>
    </div>
  );
});

const AvatarGender: React.FC<{ gender: number; onChange: (gender: number) => void }> =
  memo((props) => {
    const checkboxData = [
      {
        label: t('merror.createava.male'),
        value: 1,
      },
      {
        label: t('merror.createava.female'),
        value: 2,
      },
    ];
    return (
      <div className="flex-1 rounded-[8px]">
        <div className="text-[14px] font-[500] mb-[5px]">
          {t('merror.createava.gender')} :
        </div>
        <div className="flex flex-1 items-center border-[#43444A] border-[1px] rounded-[6px] px-[22px] py-[10px]">
          <div className="flex gap-[32px] h-[32px]">
            {checkboxData.map((item) => {
              return (
                <div
                  key={item.value}
                  className="flex items-center gap-[6px] cursor-pointer transition-all"
                  onClick={() => {
                    props.onChange(item.value);
                  }}
                >
                  <div
                    className={`transition-colors w-[16px] h-[16px] border border-app rounded-full ${props.gender === item.value ? 'bg-app-accent' : ''}`}
                  ></div>
                  <span className="text-[12px] font-[400]">{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  });
const AvatarCloneVoice: React.FC<{
  voiceClone: boolean;
  duration: number | string;
  onChange: (voiceClone: boolean) => void;
}> = (props) => {
  return (
    <div className=" flex items-center gap-[6px]">
      <span className="text-[14px] font-[500] leading-[24px] shrink-0">
        {t('merror.createava.clonevoice')} :
      </span>
      <Switch
        className={styles['avatar-switch']}
        checked={props.voiceClone}
        onChange={(checked) => {
          if (!props.voiceClone && Number(props.duration) < 10) {
            message.warning(t('The-voice-clone-requires-uploading-files-at-least'));
            return;
          }
          props.onChange(checked);
        }}
      />
    </div>
  );
};
const AvatarPrompt: React.FC<{
  prompt: string;
  url: string;
  onChange: (value: string) => void;
  onPromptChange: (value: { positive: string; negative: string }) => void;
}> = memo((props) => {
  const { prompt, url, onChange, onPromptChange } = props;
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const promptText = useRef(prompt);
  const abortController = useRef<AbortController | null>(null);

  const resetPrompt = () => {
    if (abortController.current) {
      abortController.current.abort();
    }
    setLoading(false);
  };
  useEffect(() => {
    return () => {
      resetPrompt();
    };
  }, []);

  // const promptOptimize = async () => {
  //   if (disabled || loading) return;
  //   if (!url) {
  //     message.error(t('Please-upload-photo'));
  //     return;
  //   }

  //   abortController.current = new AbortController();
  //   try {
  //     if (isCnArea) {
  //       await handleTextVerify(promptText.current);
  //     }

  //     setLoading(true);

  //     const response = await fetch('/api/chat', {
  //       method: 'POST',
  //       body: JSON.stringify({
  //         prompt: promptText.current,
  //         fileUrl: url,
  //       }),
  //       signal: abortController.current.signal,
  //     });

  //     const respData = await response.json();
  //     if (respData.success) {
  //       promptText.current = respData.prompt.positive;
  //       onPromptChange(respData.prompt);
  //     } else {
  //       message.error(t('Prompt-request-error'));
  //     }
  //   } catch (error: any) {
  //     console.error(error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  return (
    <div className="flex flex-col gap-[10px] rounded-[8px]">
      <div className="flex justify-between items-center">
        <span className=" text-[16px] font-[500] leading-[22px] py-[4px] text-app-primary">
          {t('Prompt')}
          <span className="text-[13px] font-[400] leading-[22px] text-[#A3A3A3]">
            （{t('options')}）
          </span>
        </span>
        {/* <div
          className={`select-none px-[8px] h-[32px] flex justify-center items-center transition-all gap-[4px] rounded-[6px] bg-gradient-to-r from-[#616CF3] to-[#8952FF] 
            ${disabled || loading ? 'cursor-not-allowed' : 'cursor-pointer'} ${disabled ? 'opacity-60' : 'opacity-100'}`}
          onClick={promptOptimize}
        >
          {loading ? PromptLoadingIcon : PromptDefaultIcon}
          <span>{t('Prompt-Optimisation')}</span>
          {loading && (
            <Tooltip
              classNames={{
                root: 'small-tooltip',
              }}
              title={t('Exit-AI-Generate')}
              placement="bottom"
              color="#43444A"
            >
              <span className="cursor-pointer" onClick={resetPrompt}>
                <ClosePromptIcon />
              </span>
            </Tooltip>
          )}
        </div> */}
      </div>
      <div className="border-[#43444A] border-[1px] rounded-[6px]">
        <Input.TextArea
          value={prompt}
          disabled={loading}
          maxLength={300}
          style={{
            height: 144,
            resize: 'none',
            fontSize: '14px',
            fontWeight: 400,
            fontFamily: "'Inter', sans-serif",
          }}
          placeholder={t('Describe-the-desired-Avatar-actions-poses-etc')}
          className={styles['avatar-prompt-input-new']}
          // maxLength={1000}
          onChange={(e) => {
            if (e.target.value.trim().length > 0) {
              setDisabled(false);
            } else {
              setDisabled(true);
            }
            promptText.current = e.target.value;
            onChange(e.target.value);
          }}
        />
        <div
          className={`bg-inherit ${loading ? styles['avatar-prompt-count-loading'] : ''} relative top-[-10px] 
          py-[4px] text-[14px] text-[#a3a3a3] rounded-bl-[6px] rounded-br-[6px] text-right pr-[10px]`}
        >
          {promptText.current.length}/300
        </div>
      </div>
    </div>
  );
});

const AvatarForm: React.FC<AvatarFormProps> = memo(
  ({ onChange, duration, url, mode }) => {
    const [avatarData, setAvatarData] = usePartial<AvatarFormData>(defaultAvatarData);

    useEffect(() => {
      if (!duration) {
        setAvatarData({ voiceClone: false });
      }
    }, [duration]);

    const tipData = [
      t('merror.createava.clonevoice.solo'),
      t('merror.createava.clonevoice.4'),
      t('merror.createava.clonevoice.2'),
      t('merror.createava.clonevoice.5'),
      t('merror.createava.clonevoice.3'),
      t('merror.createava.clonevoice.6'),
    ];
    useEffect(() => {
      onChange(avatarData);
    }, [avatarData]);
    const changeName = useCallback((avatarName: string) => {
      setAvatarData({ avatarName });
    }, []);
    const changeGender = useCallback((gender: number) => {
      setAvatarData({ gender });
    }, []);
    const changeSwitch = useCallback((voiceClone: boolean) => {
      setAvatarData({ voiceClone });
    }, []);
    const changePrompt = useCallback((prompt: string) => {
      setAvatarData({ prompt });
    }, []);
    const promptGenerateChange = useCallback(
      (value: { positive: string; negative: string }) => {
        setAvatarData({ prompt: value.positive, negative_prompt: value.negative });
      },
      [],
    );
    return (
      <div className="py-[14px] flex flex-col gap-[20px]">
        <div className=" flex gap-[10px]">
          <AvatarName name={avatarData.avatarName} onChange={changeName} />
          <AvatarGender gender={avatarData.gender} onChange={changeGender} />
        </div>

        {mode === 'video' ? (
          <div className="pb-[10px] flex gap-[14px]">
            <AvatarCloneVoice
              duration={duration}
              voiceClone={avatarData.voiceClone}
              onChange={changeSwitch}
            />
            {avatarData.voiceClone && (
              <div className="flex-1 px-[12px] flex flex-wrap gap-y-[2px]">
                {tipData.map((item, index) => {
                  return (
                    <div key={index} className="flex w-1/2  gap-[8px]">
                      {index % 2 === 0 ? <YesIcon /> : <NoIcon />}
                      <span className="text-[12px] leading-[24px] font-[400]">
                        {item}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <AvatarPrompt
            url={url}
            prompt={avatarData.prompt}
            onChange={changePrompt}
            onPromptChange={promptGenerateChange}
          />
        )}
      </div>
    );
  },
);

export default AvatarForm;

const PromptDefaultIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
  >
    <path
      d="M5.34434 12.508L5.39934 12.398C5.6285 11.9534 5.99059 11.5913 6.43517 11.3621L6.54517 11.3071C6.98059 11.0871 6.98059 10.4638 6.54517 10.2392L6.43517 10.1842C5.99059 9.95506 5.6285 9.59297 5.39934 9.14839L5.34434 9.03839C5.12434 8.60297 4.501 8.60297 4.27642 9.03839L4.22142 9.14839C3.99225 9.59297 3.63017 9.95506 3.18559 10.1842L3.07559 10.2392C2.64017 10.4592 2.64017 11.0826 3.07559 11.3071L3.18559 11.3621C3.63017 11.5913 3.99225 11.9534 4.22142 12.398L4.27642 12.508C4.49642 12.9434 5.11975 12.9434 5.34434 12.508Z"
      fill="white"
    />
    <path
      d="M18.4664 16.1976L18.3564 16.1426C17.9118 15.9134 17.5498 15.5513 17.3206 15.1067L17.2656 14.9967C17.0456 14.5613 16.4223 14.5613 16.1977 14.9967L16.1427 15.1067C15.9135 15.5513 15.5514 15.9134 15.1068 16.1426L14.9968 16.1976C14.5614 16.4176 14.5614 17.0409 14.9968 17.2655L15.1068 17.3205C15.5514 17.5496 15.9135 17.9117 16.1427 18.3563L16.1977 18.4663C16.4177 18.9017 17.041 18.9017 17.2656 18.4663L17.3206 18.3563C17.5498 17.9117 17.9118 17.5496 18.3564 17.3205L18.4664 17.2655C18.9018 17.0455 18.9018 16.4221 18.4664 16.1976Z"
      fill="white"
    />
    <path
      d="M5.69267 6.77422L5.83934 6.84756C6.29767 7.08131 6.66434 7.44797 6.89809 7.90631L6.97142 8.05297C7.2235 8.54797 7.72767 8.85506 8.28225 8.85506C8.83684 8.85506 9.341 8.54797 9.59767 8.05297L9.671 7.90631C9.90475 7.44797 10.2714 7.08131 10.7298 6.84756L10.8764 6.77422C11.3714 6.52214 11.6785 6.01797 11.6785 5.46339C11.6785 4.90881 11.3714 4.40464 10.8764 4.14797L10.7298 4.07464C10.2714 3.84089 9.90475 3.47422 9.671 3.01589L9.59767 2.86922C9.34559 2.37422 8.84142 2.06714 8.28225 2.06714C7.72309 2.06714 7.2235 2.37422 6.96684 2.86922L6.8935 3.01589C6.65975 3.47422 6.29309 3.84089 5.83475 4.07464L5.68809 4.14797C5.19309 4.40006 4.886 4.90422 4.886 5.46339C4.886 6.02256 5.19309 6.52214 5.68809 6.77881L5.69267 6.77422ZM6.316 5.37172L6.46267 5.29839C7.18225 4.93172 7.75517 4.35881 8.12184 3.63922L8.19517 3.49256C8.19517 3.49256 8.22267 3.43756 8.28225 3.43756C8.34184 3.43756 8.36475 3.47422 8.36934 3.49256L8.44267 3.63922C8.80934 4.35881 9.38225 4.93172 10.1018 5.29839L10.2485 5.37172C10.2485 5.37172 10.3035 5.39922 10.3035 5.45881C10.3035 5.51839 10.2623 5.54131 10.2485 5.54589L10.1018 5.61922C9.38225 5.98589 8.80934 6.55881 8.44267 7.27839L8.36934 7.42506C8.36934 7.42506 8.34184 7.48006 8.28225 7.48006C8.22267 7.48006 8.19975 7.43881 8.19517 7.42506L8.12184 7.27839C7.75517 6.55881 7.18225 5.98589 6.46267 5.61922L6.316 5.54589C6.316 5.54589 6.261 5.51839 6.261 5.45881C6.261 5.39922 6.30225 5.37631 6.316 5.37172Z"
      fill="white"
    />
    <path
      d="M19.3831 5.79797L18.4939 4.90881C18.1364 4.55131 17.6598 4.35422 17.1556 4.35422C16.6514 4.35422 16.1748 4.55131 15.8173 4.90881L6.29767 14.4284C6.29767 14.4284 6.2885 14.4421 6.28392 14.4513C6.22892 14.5109 6.18309 14.5751 6.151 14.6484V14.6576L4.82642 17.8155C4.62017 18.3059 4.73475 18.8651 5.11517 19.2409C5.36267 19.4838 5.68809 19.6121 6.02267 19.6121C6.19684 19.6121 6.37559 19.5755 6.54517 19.5021L9.63892 18.1455L9.65725 18.1363C9.73059 18.1042 9.79017 18.0584 9.84517 18.008C9.84975 18.0034 9.85892 17.9988 9.86809 17.9942L19.3877 8.47464C19.7452 8.11714 19.9423 7.64047 19.9423 7.13631C19.9423 6.63214 19.7452 6.15547 19.3877 5.79797H19.3831ZM9.37767 16.5367L7.75517 14.9142L14.8868 7.78256L16.5093 9.40506L9.37767 16.5367ZM18.4114 7.50297L17.481 8.43339L15.8585 6.81089L16.7889 5.88047C16.9814 5.68339 17.3252 5.68339 17.5223 5.88047L18.4114 6.76964C18.5077 6.86589 18.5627 6.99881 18.5627 7.13631C18.5627 7.27381 18.5077 7.40214 18.4114 7.50297Z"
      fill="white"
    />
  </svg>
);

const PromptLoadingIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
  >
    <g>
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 11 11.1667"
        to="360 11 11.1667"
        dur="1s"
        repeatCount="indefinite"
      />
      <circle cx="11.0002" cy="11.1667" r="8.16665" stroke="white" strokeWidth="2" />
      <path
        d="M19.1481 11.6641C19.7013 11.6979 20.1829 11.2758 20.156 10.7223C20.1098 9.7694 19.915 8.82751 19.5768 7.93111C19.1203 6.72096 18.4128 5.62113 17.5009 4.70389C16.5891 3.78665 15.4934 3.07276 14.286 2.60914C13.0785 2.14552 11.7866 1.94267 10.4952 2.01392C9.20377 2.08517 7.94204 2.4289 6.79293 3.02253C5.64381 3.61615 4.63333 4.44624 3.82788 5.45823C3.02244 6.47021 2.44027 7.64119 2.11964 8.89421C1.88213 9.82237 1.7921 10.78 1.85095 11.7321C1.88514 12.2853 2.41025 12.6518 2.95639 12.5574C3.50252 12.463 3.86123 11.9428 3.84403 11.3888C3.8232 10.7178 3.89681 10.0454 4.06407 9.39177C4.3145 8.41311 4.7692 7.49852 5.39829 6.70812C6.02738 5.91771 6.81661 5.26938 7.71412 4.80573C8.61163 4.34207 9.5971 4.0736 10.6058 4.01796C11.6144 3.96231 12.6234 4.12074 13.5665 4.48285C14.5096 4.84496 15.3653 5.40254 16.0776 6.11894C16.7898 6.83535 17.3423 7.69437 17.6989 8.63955C17.937 9.27083 18.0842 9.931 18.1373 10.6003C18.1811 11.1528 18.5949 11.6304 19.1481 11.6641Z"
        fill="url(#paint0_linear_8446_66196)"
      />
    </g>
    <defs>
      <linearGradient
        id="paint0_linear_8446_66196"
        x1="20.1668"
        y1="11.1667"
        x2="2.75016"
        y2="12.5417"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#3B49FF" />
        <stop offset="1" stopColor="#3B49FF" stopOpacity="0" />
      </linearGradient>
    </defs>
  </svg>
);

const ClosePromptIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="18" height="18" rx="4" fill="black" fillOpacity="0.4" />
    <path
      d="M12 12L9 9M9 9L6 6M9 9L12 6M9 9L6 12"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
